import { Button, Flex, Typography, message } from "antd";
import { useState } from "react";
import { useAppSelector } from "../../hooks/redux-hooks";
import { membersService } from "../../service/members.service";
import { UserTier } from "../../slices/authSlice";

const { Text, Title } = Typography;

const BillingInactiveModal = () => {
  const { userInfo } = useAppSelector((state) => state.auth);
  const [loading, setLoading] = useState(false);

  // Include is_group_admin: subscription deletion downgrades the payer to
  // FREE tier, and they must still get this screen (not the solo paywall).
  const isGroupUser = userInfo.tier === UserTier.GROUP || userInfo.is_group_admin;
  const isBillingInactive = isGroupUser && userInfo.group_billing_active === false;
  const isAdmin = userInfo.is_group_admin;

  const handleReactivate = async () => {
    setLoading(true);
    // Open synchronously in the click gesture — Safari blocks window.open
    // after an await. Billing period is omitted so the backend restores the
    // group's previous cadence.
    const billingTab = window.open("", "_blank");
    try {
      // Backend routes: overdue invoice, billing portal, or new subscription.
      const { payment_link } = await membersService.generateGroupPaymentLink();
      if (billingTab) {
        billingTab.location.href = payment_link;
      } else {
        window.location.href = payment_link;
      }
    } catch {
      billingTab?.close();
      message.error("Couldn't open the billing page. Please try again or contact sales@getnudgeai.com.");
    } finally {
      setLoading(false);
    }
  };

  if (!isBillingInactive) return null;

  const helpLine = (
    <Text type="secondary" style={{ fontSize: 13 }}>
      Need help? Contact sales@getnudgeai.com
    </Text>
  );

  // Takeover that sits BELOW the app header (64px) so the existing header —
  // and its log out / contact us controls — stays visible and usable. Covers
  // content and banners beneath it, but stays UNDER antd's overlay base
  // (z-index 1000): any antd modal (e.g. the "New Version Available" update
  // modal) must render fully in front, or its full-viewport mask greys the
  // header while its dialog hides under this takeover — an unexplained,
  // unclickable grey band. The solo paywall isn't covered but suppressed at
  // the source in AuthLayout while billing is inactive.
  return (
    <div
      style={{
        position: "fixed",
        top: 64,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 999,
        backgroundColor: "#FFFFFF",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
      }}
    >
      <Flex vertical gap={20} align="center" style={{ maxWidth: 460, textAlign: "center" }}>
        <Title level={3} style={{ margin: 0 }}>
          Subscription Inactive
        </Title>
        {isAdmin ? (
          <>
            <Text style={{ fontSize: 16, color: "#4B5563" }}>
              Your group's subscription is inactive. Reactivate it to restore access for you and your whole team —
              access is restored automatically once payment goes through.
            </Text>
            <Button
              type="primary"
              size="large"
              loading={loading}
              onClick={handleReactivate}
              style={{ minWidth: 240, height: 48 }}
            >
              Reactivate Subscription
            </Button>
            {helpLine}
          </>
        ) : (
          <>
            <Text style={{ fontSize: 16, color: "#4B5563" }}>
              Your group's subscription is currently inactive. Please contact your group administrator to restore
              access.
            </Text>
            {userInfo.group_name && <Text type="secondary">Group: {userInfo.group_name}</Text>}
            {helpLine}
          </>
        )}
      </Flex>
    </div>
  );
};

export default BillingInactiveModal;
